import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import Logo from "./Logo";

export default function Footer() {
  const { t, lang } = useI18n();
  const pt = lang === "pt";

  const productLinks = [
    { to: "/mercado", label: t.nav.market },
    { to: "/comparador", label: t.nav.comparator },
    { to: "/simulador", label: t.nav.simulator },
    { to: "/geografia", label: t.nav.geography },
    { to: "/assistente", label: t.nav.ai },
    { to: "/watchlist", label: pt ? "Watchlist e Alertas" : "Watchlist & Alerts" },
    { to: "/planos", label: t.nav.pricing },
  ];

  const companyLinks = [
    { to: "/como-funciona", label: pt ? "Como funciona" : "How it works" },
    { to: "/sobre", label: pt ? "Quem somos" : "About us" },
    { to: "/avaliacoes", label: pt ? "Depoimentos e Reclamações" : "Reviews & Complaints" },
    { to: "/glossario", label: pt ? "Glossário" : "Glossary" },
    { to: "mailto:support@goldora.global", label: pt ? "Falar com suporte" : "Contact support", external: true },
  ];

  const legalLinks = [
    { to: "/privacidade", label: pt ? "Privacidade" : "Privacy" },
    { to: "/termos", label: pt ? "Termos de uso" : "Terms of service" },
    { to: "/conta/privacidade", label: pt ? "Meus dados (LGPD/GDPR)" : "My data (GDPR)" },
  ];

  return (
    <footer className="border-t border-white/5 mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="font-brand text-xl tracking-[0.14em] brand-metal">GOLDORA</span>
          </div>
          <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.24em] text-gold-500">
            {pt ? "Visão global. Você no controle." : "Global vision. You in control."}
          </p>
          <p className="mt-4 text-sm text-neutral-400 max-w-md">
            {pt
              ? "Plataforma global de inteligência de decisão financeira. Para quem prefere decidir sozinho."
              : "Global financial decision intelligence platform. For those who prefer to decide on their own."}
          </p>
          <p className="mt-6 text-xs text-neutral-600 max-w-md leading-relaxed" data-testid="footer-disclaimer">
            {pt
              ? "Conteúdo puramente educacional e de inteligência de mercado. A GOLDORA não oferece aconselhamento de investimento. Retornos passados não garantem retornos futuros."
              : "Purely educational and market intelligence content. GOLDORA does not provide investment advice. Past returns do not guarantee future returns."}
          </p>
        </div>

        <div data-testid="footer-product">
          <h4 className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
            {pt ? "Produto" : "Product"}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-neutral-300">
            {productLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-gold-300 transition-colors" data-testid={`footer-link-${l.to.replace(/\//g, "")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-company">
          <h4 className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
            {pt ? "Empresa" : "Company"}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-neutral-300">
            {companyLinks.map((l) => (
              <li key={l.to}>
                {l.external ? (
                  <a href={l.to} className="hover:text-gold-300 transition-colors" data-testid={`footer-link-${l.to.replace(/[^a-z0-9]/gi, "")}`}>
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} className="hover:text-gold-300 transition-colors" data-testid={`footer-link-${l.to.replace(/\//g, "")}`}>
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-legal">
          <h4 className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
            {pt ? "Legal" : "Legal"}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-neutral-300">
            {legalLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-gold-300 transition-colors" data-testid={`footer-link-${l.to.replace(/\//g, "")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-5 text-[11px] font-mono text-neutral-500 leading-relaxed" data-testid="footer-emails">
            <div><a href="mailto:support@goldora.global" className="hover:text-gold-300 transition-colors" data-testid="footer-email-support">support@goldora.global</a></div>
            <div><a href="mailto:privacy@goldora.global" className="hover:text-gold-300 transition-colors">privacy@goldora.global</a></div>
            <div><a href="mailto:legal@goldora.global" className="hover:text-gold-300 transition-colors">legal@goldora.global</a></div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between text-xs text-neutral-600">
          <span>© {new Date().getFullYear()} GOLDORA</span>
          <span className="font-mono uppercase tracking-widest">v0.3 — mvp</span>
        </div>
      </div>
    </footer>
  );
}
